import React, { useRef, useCallback, forwardRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import Link from './Link'
import theme from '../style/theme'
import { span } from '../style/span'
import { vw } from '../style/vw'
import { quad, back } from '../style/eases'
import { useStickyCursor, useHoverCursor } from './Cursor'
import CartButton from './Cart/CartButton'
import { useDispatch, useSelector } from 'react-redux'
import {
  isMenuOpen,
  openMenu,
  closeMenu
  // toggleCartDialog,

} from '../redux/slices/layout'
import useReversingLottieAnimation from '../hooks/useReversingLottieAnimation'
import logoData from '../animation/logo.json'
import { getTopCta } from '../redux/slices/content/selectors'
import { ReactComponent as CloseIcon } from '../images/close-small.svg'
import RichText from './RichText'

const aspectRatio = logoData.w / logoData.h

const Header = forwardRef((props, ref) => {
  const menuOpen = useSelector(isMenuOpen)
  const classes = useStyles({ menuOpen })
  const linkRef = useRef()
  const menuRef = useRef()
  const dispatch = useDispatch()
  const logoRef = useReversingLottieAnimation(logoData, true, 0.7)
  const loginBtnRef = useRef()
  const topCta = useSelector(getTopCta)
  const [topCtaOpen, setTopCtaOpen] = useState(topCta?.enabled)
  useStickyCursor(linkRef)
  useHoverCursor(menuRef)
  useHoverCursor(loginBtnRef)

  const toggleMenu = useCallback(() => {
    dispatch(menuOpen ? closeMenu() : openMenu())
  }, [dispatch, menuOpen])

  const toggleTopCta = useCallback(() => {
    setTopCtaOpen(!topCtaOpen)
  }, [topCtaOpen])

  // const handleOpenMiniCart = useCallback(() => {
  //   dispatch(toggleCartDialog())
  // }, [])

  const handleLoginClick = () => {
    const loginUrl = 'https://discovered-wildfoods.myshopify.com/account/login'
    window.location.href = loginUrl
  }
  return (
    <header className={cn(classes.header, { blend: !menuOpen, ctaClosed: !topCtaOpen })} ref={ref}>
      {topCta?.enabled && (
        <div className={cn(classes.topCta, { open: topCtaOpen })}>
          <div>
            <p>{topCta.title} — </p>
            {topCta.text && <RichText content={topCta.text.text} />}
          </div>
          <button className={classes.topCtaClose} onClick={toggleTopCta}>
            <CloseIcon ref={menuRef} />
          </button>
        </div>
      )}
      <Link to='/' ref={linkRef}>
        <span className={classes.logo} ref={logoRef} />
      </Link>

      <div className={classes.menuItems}>
        <button className={classes.menuButton} ref={menuRef} onClick={toggleMenu}>
          <span>{menuOpen ? 'Close' : 'Menu'}</span>
        </button>
        <button className={cn(classes.menuButton)} onClick={handleLoginClick} ref={loginBtnRef}>
          <span>Login</span>
        </button>
        {!menuOpen ? <CartButton /> : null}
      </div>
    </header>
  )
})

const useStyles = createUseStyles({
  topCta: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 200,
    width: '100%',
    borderBottom: [1, 'solid', 'rgba(255,255,255,0.25)'],
    height: '0',
    display: 'flex',
    alignItems: 'center',
    transition: `height 0.25s ${quad.out}`,
    overflow: 'hidden',
    padding: [0, vw(12)],

    '&.open': {
      height: 'auto',
      minHeight: '30px',
      transition: `height 0.5s ${back.out}`,
      [theme.breakpoints.up('md')]: {
        height: vw(35, 'desktop')
      }
    },

    '& > div': {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '& > p, & > div': {
        textAlign: 'center',
        color: 'currentColor',
        fontSize: '12px',
        fontFamily: theme.fonts.headings,
        fontWeight: theme.fonts.headingsFontWeight,
        margin: 0,
        display: 'inline',
        [theme.breakpoints.up('md')]: {
          fontSize: vw(13, 'desktop')
        },

        '& a': {
          textDecoration: 'underline'
        }
      },

      '& > p:first-child': {
        display: 'none'
      },
      [theme.breakpoints.up('md')]: {
        '& p:first-child': {
          display: 'inline'
        }
      }
    },

    '& button': {
      width: vw(24),
      height: vw(24),
      color: 'currentColor',
      display: 'inline-block',
      [theme.breakpoints.up('md')]: {
        width: vw(24, 'desktop'),
        height: vw(24, 'desktop'),
        marginLeft: vw(16, 'desktop')
      }
    }
  },
  header: {
    pointerEvents: 'none',
    zIndex: theme.zIndex.header,
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    margin: [span(2), span(1), 0],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.colors.white,
    transition: `margin 0.25s ${quad.out}`,

    [theme.breakpoints.up('md')]: {
      margin: [span(1.25, 'md'), span(1, 'md'), 0]
    },

    '&.blend': {
      mixBlendMode: 'difference'
    },
    '&.ctaClosed': {
      margin: [span(1), span(1), 0],

      [theme.breakpoints.up('md')]: {
        margin: [span(1, 'md'), span(1, 'md'), 0]
      }
    },
    '& > *': {
      pointerEvents: 'all'
    }
  },
  menuButton: {
    color: 'currentColor',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: vw(14),
    letterSpacing: '0.02em',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop')
    }
  },
  logo: {
    display: 'block',
    height: vw(26),
    width: vw(30 * aspectRatio * 0.89),
    [theme.breakpoints.up('md')]: {
      height: vw(30, 'desktop'),
      width: vw(30 * aspectRatio, 'desktop')
    }
  },
  menuItems: {
    display: 'flex',
    alignItems: 'center',
    gap: span(0.5),

    [theme.breakpoints.up('md')]: {
      gap: span(0.25)
    }
  },
  cartButton: {
    display: 'flex',
    alignItems: 'center',
    visibility: ({ menuOpen }) => menuOpen ? 'hidden' : 'visible',
    [theme.breakpoints.up('md')]: {

    }
  },
  lineItems: {
    display: 'none',
    marginLeft: vw(6),
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.colors.primary,
    backgroundColor: theme.colors.white,
    height: vw(24),
    width: vw(24),
    borderRadius: '50%',
    transform: 'scale(0)',
    fontSize: vw(12),
    transition: `transform 0.25s ${quad.out}`,
    '&.show': {
      display: 'flex',
      transition: `transform 0.5s ${back.out}`,
      transform: 'scale(1)'
    },
    '&.small': {
      fontSize: vw(10),
      [theme.breakpoints.up('md')]: {
        fontSize: vw(10, 'desktop')
      }
    },
    '& span': {
      fontFamily: theme.fonts.body,
      fontWeight: theme.fonts.bodyFontWeight,
      lineHeight: 1,
      paddingBottom: '0.1em'
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: vw(6, 'desktop'),
      height: vw(24, 'desktop'),
      width: vw(24, 'desktop'),
      fontSize: vw(12, 'desktop')
    }
  }
}, { name: 'Header' })

export default Header
